import { React, useState, useEffect, Drawer, DialogActions, Box, Typography, Divider, IconButton, ArrowBackIosIcon, Default_Text_Color } from '../../utils/globalImports';
import { CustomButton, CustomTextField, CustomRadioButton, validationMessages } from '../../utils/globalImports';

interface RegionData {
  regionName: string;
  description: string;
  status: string;
}

interface CustomAddRegionModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: RegionData, setBackendErrors?: (errors: any) => void) => void;
  regionData?: RegionData;  
  handleContentChange:(state:boolean)=>void
}

const validationMessageStyle = {
  color: "rgba(255, 0, 0, 0.99)",
  fontSize: "11px",
  paddingLeft: "10px",
  paddingBottom: "10px",
};

const CustomAddRegionModal: React.FC<CustomAddRegionModalProps> = ({ open, onClose, onSubmit, regionData,handleContentChange }) => {
  const [formData, setFormData] = useState<RegionData>({
    regionName: '',
    description: '',
    status: 'Active',
  });

  const [errors, setErrors] = useState({
    RegionName: '',
    description: '',
  });

  const [backendErrors, setBackendErrors] = useState<{ [key: string]: string }>({});

  // Validate input fields
  const validate = () => {
    let isValid = true;
    const errors = { RegionName: '', description: '' };

    if (!formData.regionName.trim()) {
      errors.RegionName = validationMessages.common_required("Client Region Name");
      isValid = false;
    } else if (formData.regionName.length > 255) {
      errors.RegionName = validationMessages.common_characterLimit("Client Region Name", 255);
      isValid = false;
    } else if (!/^[A-Za-z\s]+$/.test(formData.regionName)) { 
      errors.RegionName = validationMessages.common_invalidString("Client Region Name");
      isValid = false;
    }

    if(formData.description.length > 5000) {
      errors.description = validationMessages.common_characterLimit("Description", 5000);
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (validate()) {
      await onSubmit(formData, setBackendErrors);
    }
  };

  // Populate fields if editing a region
  useEffect(() => {
    if (open) {
      if (regionData) {
        setFormData({
          regionName: regionData.regionName || '',
          description: regionData.description || '',
          status: regionData.status || 'Active',
        });
      } else {
        setFormData({
          regionName: '',
          description: '',
          status: 'Active',
        });
      }
      setErrors({
        RegionName: '', description: ''
      });
      setBackendErrors({});
    }
  }, [open, regionData]);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    handleContentChange(true);
  };

  const handleNavigate = () => {
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        width: 350,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 500,
          height: '100%',
          paddingLeft: 2,
          paddingRight: 2,
          paddingTop: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.86)',
        },
      }}
    >
      <Box>
        <Typography
          sx={{
            color: Default_Text_Color,
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            mt: 2,
            position: 'relative',
            fontWeight: 500,
            fontSize: 22
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              left: 0,
              width: '40px',
              height: '40px',
              backgroundColor: 'transparent',
            }}
            onClick={handleNavigate}
          >
            <ArrowBackIosIcon sx={{ color: '#1e66ab', fontSize: '15px' }} />
          </IconButton>
          {regionData ? 'Edit Client Region' : 'Add Client Region'}
        </Typography>

        <Divider sx={{ mb: 3, mx: "-15px" }}></Divider>

        {/* Client Region Name */}
        <CustomTextField
          label="Client Region Name"
          name="regionName"
          value={formData.regionName}
          onChange={handleChange}
          sx={{ mb: 1, mt:1.5, backgroundColor: '#fff' }}
          required={true}
        />
        {errors.RegionName && (
          <Typography style={validationMessageStyle} role="alert">
            {errors.RegionName}
          </Typography>
        )}
        {backendErrors.regionName && <Typography style={validationMessageStyle}>{backendErrors.regionName}</Typography>}

        {/* Description */}
        <CustomTextField
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          sx={{ mb: 2, mt:1.5, backgroundColor: '#fff',
            '& .MuiInputBase-root': {
              height: 'auto',  // Make sure that the outer container can auto adjust its height
            },
            '& .MuiOutlinedInput-input': {
              minHeight: 100, // Set the minimum height of the input field (works for textareas as well)
              padding: '10px', // Add padding to avoid text sticking to the edges
            }, }}
          required={false}
          isTextArea={true}
        />
        {errors.description && (
          <Typography style={validationMessageStyle} role="alert">
            {errors.description}
          </Typography>
        )}

        {/* Status */}
        <Typography sx={{ color: '#1e66ab', fontSize: 14, fontWeight: 500, ml: 1, mt: 1 }}>Status</Typography>
        <Box sx={{ display: 'flex' }}>
          <CustomRadioButton
            label="Active"
            value="Active"
            name="status"
            checked={formData.status === 'Active'}
            onChange={handleChange}
            required={true}
          />
          <CustomRadioButton
            label="Inactive"
            value="Inactive"
            name="status"
            checked={formData.status === 'Inactive'}
            onChange={handleChange}
            required={true}
          />
        </Box>

        {/* Submit Button */}
        <DialogActions>
          <CustomButton
            capsule
            sx={{
              textTransform: 'none',
              width: '282px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              mr: 'auto',
              ml: 'auto',
              mt: 2,
            }}
            onClick={handleSubmit}
          >
            {regionData ? 'Update Client Region' : 'Add Client Region'}
          </CustomButton>
        </DialogActions>
      </Box>
    </Drawer>
  );
};

export default CustomAddRegionModal;
